import { defineComponent as _defineComponent } from 'vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import { computed } from 'vue'
import { getJobLineItemsQuery } from '@/api/jobs'
import VsText from '@/components/vision/VsText.vue'
import { useGetters, useStore } from 'vuex-composition-helpers'
import { useInvoiceQuery } from '@/api/invoice'
import { ApprovalStatus } from '@/models/job'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'
import { ItemWhenSigned } from '@/models/job-signature'
import Big from 'big.js'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { useRouter } from 'vue-router/composables'

interface Props {
  jobId: string
  jobApprovalStatus: ApprovalStatus
  assignedToServiceProviderId?: string | null
  hubId: string
  readonly?: boolean
  batchId?: string | null
  showBatchLink?: boolean
  lineItemsWhenSigned?: ItemWhenSigned[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LineItemsCard',
  props: {
    jobId: null,
    jobApprovalStatus: null,
    assignedToServiceProviderId: { default: null },
    hubId: null,
    readonly: { type: Boolean, default: false },
    batchId: { default: undefined },
    showBatchLink: { type: Boolean, default: false },
    lineItemsWhenSigned: { default: () => [] }
  },
  setup(__props: any) {

const props = __props as { jobId: string, jobApprovalStatus: ApprovalStatus, assignedToServiceProviderId: string | null, hubId: string, readonly: boolean, batchId: string | null, showBatchLink: boolean, lineItemsWhenSigned: ItemWhenSigned[] }


const batchIdRef = computed(() => props.batchId)
const { data: batch } = useInvoiceQuery(batchIdRef)
const router = useRouter()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const { currentOrgHasInvoicingEntitlement } = useBusinessUnitPermissions()

const {
  data: lineItems,
  isLoading,
  isError,
  error: lineItemError,
} = getJobLineItemsQuery(props.jobId)

const formatter = useCurrencyFormatter()
const rateFormatter = useCurrencyFormatter(5)

const { hasLineItemEditApprovalAccess } = useGetters('permissions', [
  'hasLineItemEditApprovalAccess',
])

const store = useStore()
const hasLineItemRateAccessMethod = computed<
  (serviceProviderId: string | null, hubId: string) => boolean
>(() => store.getters['permissions/hasLineItemRateAccess'])
const hasLineItemRateAccess = computed(() =>
  hasLineItemRateAccessMethod.value(
    props.assignedToServiceProviderId,
    props.hubId
  )
)

const shouldShowTotal = computed(() => {
  const showNoItemsTotal =
    !props.showBatchLink ||
    (props.showBatchLink && lineItems.value && lineItems.value.length !== 0)
  return hasLineItemRateAccess.value && showNoItemsTotal && ticketIsCosted.value
})

const canAddLineItems = computed(() => {
  if (
    props.jobApprovalStatus === ApprovalStatus.APPROVED ||
    props.jobApprovalStatus === ApprovalStatus.PENDING_APPROVAL
  ) {
    return (
      !props.readonly && !isError.value && hasLineItemEditApprovalAccess.value
    )
  }

  return !props.readonly && !isLoading.value && !isError.value
})

const lineItemButtonIcon = computed(() => {
  if (lineItems.value && lineItems.value.length > 0) return ''
  return 'add'
})

const lineItemButtonLabel = computed(() => {
  if (lineItems.value && lineItems.value.length > 0) return 'Edit'
  return 'Add'
})

const lineItemSubTotal = computed(() => {
  if (!hasLineItemRateAccess.value) return ''
  if (!lineItems.value || lineItems.value.length === 0)
    return formatter.format(0)

  const total = lineItems.value.reduce((sum, { quantity, rate }) => {
    if (!quantity || !rate) return sum

    const val = new Big(quantity).mul(rate)
    const rounded = val.round(2, Big.roundHalfUp)

    return sum.plus(rounded)
  }, new Big(0))

  return formatter.format(Number(total))
})

const ticketIsCosted = computed(() => {
  if (!lineItems.value) return false

  return (
    lineItems.value.filter((lineItem) => lineItem.quantity && lineItem.rate)
      .length > 0
  )
})

const baseRoutePath = computed(() => {
  return '/jobs'
})

function getLineItemCaptionText(quantity: number | null, rate: number | null) {
  if (ticketIsCosted.value || (rate && quantity)) {
    // parseFloat eliminates trailing zeros
    return `${parseFloat(quantity?.toFixed(5) ?? '0')} × ${rateFormatter.format(
      rate ?? 0
    )}`
  }
  return ''
}

function getLineItemAmount(quantity: number | null, rate: number | null) {
  if (!hasLineItemRateAccess.value || !ticketIsCosted.value) return quantity

  return formatter.format((rate ?? 0) * (quantity ?? 0))
}

function getLineItemSigned(quantity: number | null, name: string) {
  return props.lineItemsWhenSigned.some((item) => item[name] === quantity)
}

function openLineItemBottomSheet() {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    isLineItemBottomSheetVisible: true,
  })
}

return { __sfc: true,props, batchIdRef, batch, router, jobBottomSheetStatesStore, currentOrgHasInvoicingEntitlement, lineItems, isLoading, isError, lineItemError, formatter, rateFormatter, hasLineItemEditApprovalAccess, store, hasLineItemRateAccessMethod, hasLineItemRateAccess, shouldShowTotal, canAddLineItems, lineItemButtonIcon, lineItemButtonLabel, lineItemSubTotal, ticketIsCosted, baseRoutePath, getLineItemCaptionText, getLineItemAmount, getLineItemSigned, openLineItemBottomSheet, VsWrapper, VsButton, VsHeading, VsText }
}

})